import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faList, faDatabase } from '@fortawesome/free-solid-svg-icons';
import { faFile } from '@fortawesome/free-regular-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface Feature {
  icon: IconDefinition;
  title: string;
  description: string;
}

const features: Feature[] = [
  {
    icon: faList,
    title: 'Structured Data',
    description:
      'We transform what AI sees on your site so it can actually sell to your potential customers.',
  },
  {
    icon: faFile,
    title: 'AI Business Card',
    description:
      'Dedicated file that introduces your business to every AI assistant. Like a business card for ChatGPT.',
  },
  {
    icon: faDatabase,
    title: 'AI Crawler Support',
    description:
      'We make every page readable to AI without touching the content normal people see.',
  },
];

export default function WhatWeAdd() {
  return (
    <section className="flex flex-col items-center text-center w-full">
      <div className="flex flex-col gap-3 items-center max-w-[640px] px-4 md:px-0">
        <h2 className="font-headline text-[32px] md:text-[40px] leading-[1.2] text-[#1e1c1c]">
          We make your site speak AI.
        </h2>

        <p className="font-body font-medium text-[18px] text-[rgba(30,28,28,0.7)] leading-[1.5]">
          Right now, AI can't read most of your site. We fix that without
          changing anything your customers see.
        </p>
      </div>

      {/* Desktop: side by side with dividers */}
      <div className="hidden md:flex gap-8 mt-12 w-full max-w-[960px] items-start">
        {features.map((feature, i) => (
          <React.Fragment key={feature.title}>
            {i > 0 && (
              <div className="bg-[rgba(30,28,28,0.1)] h-[200px] w-px shrink-0" />
            )}
            <div className="flex flex-col items-center text-center flex-1 gap-5">
              <div className="bg-[#eee] rounded-full w-12 h-12 flex items-center justify-center">
                <FontAwesomeIcon
                  icon={feature.icon}
                  className="text-[#1e1c1c] text-[18px]"
                />
              </div>
              <div className="flex flex-col gap-1">
                <div className="font-body font-medium text-[18px] leading-[1.5] text-[#1e1c1c]">
                  {feature.title}
                </div>
                <p className="font-body font-medium text-[16px] leading-[1.5] text-[rgba(30,28,28,0.7)]">
                  {feature.description}
                </p>
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Mobile: horizontal scroll cards */}
      <div className="md:hidden mt-8 w-full overflow-x-auto snap-x snap-mandatory scroll-pl-4 scrollbar-hide">
        <div className="flex gap-4">
          {features.map((feature, i) => (
            <div
              key={feature.title}
              className={`snap-start w-[280px] shrink-0 bg-white border border-[rgba(30,28,28,0.08)] rounded p-6 flex flex-col items-center text-center gap-4${i === 0 ? ' ml-4' : ''}`}
            >
              <div className="bg-[#eee] rounded-full w-12 h-12 flex items-center justify-center">
                <FontAwesomeIcon
                  icon={feature.icon}
                  className="text-[#1e1c1c] text-[18px]"
                />
              </div>
              <div className="flex flex-col gap-1">
                <div className="font-body font-medium text-[18px] leading-[1.5] text-[#1e1c1c]">
                  {feature.title}
                </div>
                <p className="font-body font-medium text-[15px] leading-[1.5] text-[rgba(30,28,28,0.7)]">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
          <div className="w-1 shrink-0" />
        </div>
      </div>
    </section>
  );
}
